import { useEffect, useState, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { PAPERFORM_URL, IGNITE_CONSULT_URL } from "@/lib/leads";

/**
 * Slim sticky CTA bar that appears once the user scrolls past the hero
 * and hides again when the final CTA section enters view (so it doesn't
 * repeat itself). Sits below the floating chat widget (chat: bottom-20;
 * this bar: bottom-0).
 */
export default function StickyAssessmentBar({
    showAfterSelector = '[data-testid="ignite-hero"]',
    hideOnSelector = '[data-testid="ignite-final-section"]',
}) {
    const [visible, setVisible] = useState(false);
    const finalInView = useRef(false);
    const pastHero = useRef(false);

    useEffect(() => {
        const heroEl = document.querySelector(showAfterSelector);
        const finalEl = document.querySelector(hideOnSelector);
        if (!heroEl && !finalEl) return;

        const update = () => {
            setVisible(pastHero.current && !finalInView.current);
        };

        const heroObs = heroEl
            ? new IntersectionObserver(
                  ([e]) => {
                      // Past hero when it's no longer in the viewport (scrolled below)
                      pastHero.current = !e.isIntersecting && e.boundingClientRect.top < 0;
                      update();
                  },
                  { threshold: 0 }
              )
            : null;

        const finalObs = finalEl
            ? new IntersectionObserver(
                  ([e]) => {
                      finalInView.current = e.isIntersecting;
                      update();
                  },
                  { threshold: 0.1 }
              )
            : null;

        heroObs?.observe(heroEl);
        finalObs?.observe(finalEl);

        return () => {
            heroObs?.disconnect();
            finalObs?.disconnect();
        };
    }, [showAfterSelector, hideOnSelector]);

    return (
        <div
            data-testid="sticky-assessment-bar"
            aria-hidden={!visible}
            className={`fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm transition-transform duration-300 ${
                visible ? "translate-y-0" : "translate-y-full"
            }`}
        >
            <div className="container-x py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs sm:text-sm text-ink-soft text-center sm:text-left">
                    <span className="text-foreground font-semibold">
                        Save $620+/employee
                    </span>{" "}
                    in payroll tax with the Ignite Plan.
                </p>
                <div className="flex items-center gap-4">
                    <a
                        href={IGNITE_CONSULT_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="sticky-secondary"
                        className="hidden md:inline text-xs text-ink-soft hover:text-brand underline underline-offset-4 decoration-border"
                    >
                        Book directly with Ignite Health
                    </a>
                    <a
                        href={PAPERFORM_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="sticky-primary"
                        className="inline-flex items-center gap-2 bg-brand text-brand-foreground hover:bg-brand-hover px-5 py-2.5 rounded-sm text-sm font-semibold whitespace-nowrap"
                    >
                        Get Free Assessment
                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
                    </a>
                </div>
            </div>
        </div>
    );
}
